import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from '@edl-development/edl-common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { SupervisorUserDTO } from 'src/app/model/base/supervisor-user-dto.model';
import { ObservationTypeValues } from 'src/app/model/enum/observation-type-values.enum';
import { DuplicateObservationDTO } from 'src/app/model/observation/common/duplicate-observation-dto.model';
import { UserSummaryPermissions } from 'src/app/model/observation/user-summary-permisions.model';
import { ObservationListService } from 'src/app/services/observation-list.service';
import { PaetepUserHelperService } from 'src/app/services/paetep-user-helper/paetep-user-helper.service';
import { PermissionsHelperService } from 'src/app/services/permissions-helper/permissions-helper.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
    selector: 'app-paetep-duplicate-obs-modal',
    templateUrl: './paetep-duplicate-obs-modal.component.html',
    styleUrls: ['./paetep-duplicate-obs-modal.component.scss']
})
export class PaetepDuplicateObsModalComponent implements OnInit {
    @Input() observationType?: ObservationTypeValues;
    @Input() observationId?: string;
    @Input() targetUserId?: string;
    @Input() userSummaryPermissions?: UserSummaryPermissions;

    private performanceMeasureTypes: { label: string, value: ObservationTypeValues }[] = [
        { label: "Sudent Performance Measure", value: ObservationTypeValues.SPM },
        { label: "IEP Goals Progress", value: ObservationTypeValues.IEP },
        { label: "Performance Goal", value: ObservationTypeValues.PerformanceGoal }
    ];

    public form!: FormGroup;
    public filteredPerformanceMeasureTypes: { label: string, value: ObservationTypeValues }[] = [];
    public evaluators: SupervisorUserDTO[] = [];
    public schoolYears: string[] = [];
    public busy: boolean = false;


    constructor(
        public activeModal: NgbActiveModal,
        private formBuilder: FormBuilder,
        private sessionService: SessionService,
        private permissionsHelperService: PermissionsHelperService,
        private paetepUserHelperService: PaetepUserHelperService,
        private observationListService: ObservationListService,
        private toastrService: ToastrService
    ) { }

    ngOnInit(): void {
        this.schoolYears = this.sessionService.GetSessionValue('school_years') as string[];
        const schoolYear = this.schoolYears[0];

        this.form = this.formBuilder.group({
            observationId: [this.observationId, [Validators.required]],
            observationType: [this.observationType, [Validators.required]],
            performanceMeasureType: [this.observationType, [Validators.required]],
            evalUserId: [null, [Validators.required]],
            targetUserId: [this.targetUserId, [Validators.required]],
            schoolYear: [schoolYear, [Validators.required]]
        });

        this.filteredPerformanceMeasureTypes = this.performanceMeasureTypes.filter(o => this.permissionsHelperService.canCreateObservation([o.value], this.userSummaryPermissions));
        this.loadEvaluators();
    }

    private loadEvaluators() {
        return this.paetepUserHelperService.getSelectEvaluatorOptions(
            this.targetUserId as string, 
            true,
            this.userSummaryPermissions?.observationsTargetUserCanHave ?? []
        ).then((evaluators) => {
            this.evaluators = evaluators.filter(e => this.observationType && e.observationsSupervisorCanManage.includes(this.observationType));

            this.form.controls['evalUserId'].addValidators([CustomValidators.OptionListValidator(this.evaluators.map(x => x.user.userID))]);
            if (this.evaluators.findIndex(x => x.user.userID == this.sessionService.user?.UserID) > -1) {
                this.form.get('evalUserId')?.setValue(this.sessionService.user?.UserID);
            }

            this.form.updateValueAndValidity();
        });
    }
    

    showError(controllerName: string) {
        const controller = this.form.get(controllerName);
        return !controller?.valid && controller?.touched;
    }

    showAdditionalOptionsSPM() {
        return this.performanceMeasureTypes.findIndex(p => p.value == this.form.get('observationType')?.value) !== -1
            && this.filteredPerformanceMeasureTypes.length > 0;
    }

    onSubmit({ value, valid } : { value: DuplicateObservationDTO, valid: boolean }) {
        if (valid && !this.busy) {
            this.busy = true;

            firstValueFrom(
                this.observationListService.duplicateObservation(value)
            ).then((observationSummary) => {
                this.toastrService.success("Observation Duplicated.", "Success");

                this.activeModal.close();

                this.observationListService.navigateOnCreation(value.targetUserId, observationSummary);
            }).finally(() => this.busy = false);
        }
    }
}
